import { Card, CbhIcon, DotSeparatedList, Pill, Text } from "@clipboard-health/ui-components";
import { CardContent, Stack, type Theme, useTheme } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { getPlacementDetailsPathWithoutPlacementCandidate } from "@src/appV2/redesign/Placements/paths";
import { type InterviewStatus } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { differenceInMinutes, format, isBefore, isSameMinute, startOfMinute } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { isEmpty } from "lodash";
import { useHistory } from "react-router-dom";

import { Button } from "../../components/Button";
import { formatDateRangeDuration } from "../../utils/formatDateRangeDuration";

export interface HighlightedWorkerInterviewCardProps {
  interviewId: string;
  start: Date;
  end: Date;
  status: InterviewStatus;
  placementId: string;
  workerTypes: string[];
  workplaceName: string;
  currentDate: Date;
}

function getInterviewStatusParameters(props: {
  startDate: Date;
  workerTypes: string[];
  workplaceName: string;
  currentDate: Date;
  workerTimezone: string;
  theme: Theme;
}) {
  const { startDate, workerTypes, workplaceName, currentDate, workerTimezone, theme } = props;

  let titleText = "";
  let pillText = "";
  const pillColor = theme.palette.background.tertiary;

  // Convert current date to worker's timezone
  const currentDateInWorkerTimezone = utcToZonedTime(currentDate, workerTimezone);

  const startsIn = formatDateRangeDuration({
    startDate: startOfMinute(currentDateInWorkerTimezone),
    endDate: startDate,
  });

  const interviewDetails = `${
    workerTypes.length > 0 ? workerTypes.join(", ") : "job"
  } interview at ${format(startDate, "h:mm a")} ${
    isEmpty(workplaceName) ? "" : ` at ${workplaceName}`
  }`.trim();

  const now = startOfMinute(currentDateInWorkerTimezone);
  const isInterviewStarted = isBefore(startDate, now) || isSameMinute(startDate, now);

  if (isInterviewStarted) {
    pillText = "Interview started";
    titleText = `Attend your ${interviewDetails}`;
  } else {
    pillText = `Starts in ${startsIn}`;
    titleText = `Prepare for your ${interviewDetails}`;
  }

  return { titleText, pillText, pillColor };
}

export function HighlightedWorkerInterviewCard(props: HighlightedWorkerInterviewCardProps) {
  const {
    interviewId,
    start: startDate,
    end: endDate,
    status,
    placementId,
    workerTypes,
    workplaceName,
    currentDate,
  } = props;

  const theme = useTheme();
  const history = useHistory();
  const worker = useDefinedWorker();

  const duration = differenceInMinutes(endDate, startDate);

  const { titleText, pillText, pillColor } = getInterviewStatusParameters({
    startDate,
    workerTypes,
    workplaceName,
    currentDate,
    workerTimezone: worker.tmz,
    theme,
  });

  const handleViewDetails = () => {
    logEvent(APP_V2_APP_EVENTS.BOOKED_INTERVIEW_CLICKED, {
      workerId: worker.userId,
      interviewDetails: {
        start: zonedTimeToUtc(startDate, worker.tmz).toISOString(),
        end: zonedTimeToUtc(endDate, worker.tmz).toISOString(),
        status,
        id: interviewId,
      },
      placementDetails: {
        id: placementId,
        workerTypes: workerTypes ?? [],
      },
    });

    history.push(getPlacementDetailsPathWithoutPlacementCandidate(placementId));
  };

  return (
    <Card
      sx={{
        flexGrow: 1,
        background: (theme: Theme) => theme.palette.intent?.success.gradient,
      }}
      variant="tertiary"
    >
      <CardContent onClick={handleViewDetails}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Pill size="medium" label={pillText} backgroundColor={() => pillColor} />
          <CbhIcon type="briefcase-filled" size="medium" />
        </Stack>
        <Stack direction="column" spacing={5} marginTop={12}>
          <Text variant="h5">{titleText}</Text>
          <DotSeparatedList>
            {[
              workerTypes.length > 0 ? (
                <Text key={workerTypes.join(", ")} variant="body2">
                  {workerTypes.join(", ")} interview
                </Text>
              ) : null,
              isEmpty(workplaceName) ? null : (
                <Text key={workplaceName} variant="body2">
                  {workplaceName}
                </Text>
              ),
              <Text key={duration} variant="body2">
                {duration} mins
              </Text>,
            ].filter(Boolean)}
          </DotSeparatedList>

          <Button variant="outlined" onClick={handleViewDetails}>
            View job details
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
